import useSEO from "../hooks/useSEO";
import { motion } from "framer-motion";
import { Lightbulb, Fan, Snowflake, Radar, DoorClosed, Thermometer, Users, Smartphone, Sparkles } from "lucide-react";
import PageHero from "../components/PageHero";
import SefKamHomeSection from "../components/SefKamHomeSection";
import CTASection from "../components/CTASection";
import SectionHeading from "../components/SectionHeading";
import { IMAGES } from "../data/site";

const CONTROLS = [
  { icon: Lightbulb, label: "Lights" },
  { icon: Fan, label: "Ceiling Fan" },
  { icon: Snowflake, label: "AC" },
  { icon: Radar, label: "Sensors" },
  { icon: DoorClosed, label: "Doors" },
  { icon: Thermometer, label: "Temperature" },
  { icon: Users, label: "Occupancy" },
  { icon: Smartphone, label: "Mobile App" },
];

export default function SefKamHome() {
  useSEO({
    title: "SefKam Home | AI-Powered Smart Home Platform",
    description: "SefKam Home is our vision for an AI-powered smart-home platform — control and automate everyday devices through one intelligent experience. Coming soon.",
    path: "/sefkam-home",
  });
  return (
    <>
      <PageHero
        eyebrow="SefKam Home · Coming Soon"
        title="Your home. Connected. Automated. Intelligent."
        subtitle="Our smart-home platform lets people control and automate everyday devices through one intelligent experience powered by an AI assistant."
      />

      <SefKamHomeSection compact />

      <section className="bg-white py-24 md:py-32" data-testid="sefkam-home-controls">
        <div className="container-x">
          <SectionHeading
            eyebrow="One intelligent layer"
            title="Everything in your home, working together."
            subtitle="SefKam Home is designed to bring disconnected devices into a single, adaptive experience."
            max="max-w-3xl"
          />
          <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {CONTROLS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <motion.div
                    key={c.label}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                    className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-4 text-center"
                    data-testid={`control-${i}`}
                  >
                    <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#07111F] text-cyan-300"><Icon size={19} strokeWidth={1.8} /></span>
                    <span className="text-xs font-medium text-[#07111F]">{c.label}</span>
                  </motion.div>
                );
              })}
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="pointer-events-none absolute -inset-4 rounded-[2.5rem] accent-bg opacity-20 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-slate-200">
                <img src={IMAGES.smartHome} alt="SefKam Home smart-home concept" loading="lazy" className="h-full w-full object-cover" />
              </div>
            </motion.div>
          </div>

          <div className="mt-14 flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5" data-testid="sefkam-home-disclaimer">
            <Sparkles className="shrink-0 text-violet-500" size={18} />
            <p className="text-sm text-slate-600">
              <strong className="text-[#07111F]">Coming Soon.</strong> SefKam Home is our product vision and is not yet
              commercially available. We're sharing an early look at what we're building.
            </p>
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Be first to know"
        title="Interested in SefKam Home?"
        subtitle="Tell us about your smart-home needs and we'll keep you posted as the platform develops."
        primary={{ label: "Get in Touch", to: "/contact" }}
      />
    </>
  );
}
